import { LeaderboardTrialInvitesPopupComponent } from '../leaderboard-trial-invites-popup/leaderboard-trial-invites-popup.component';
import { PopupService } from '@app/shared/popup';
import { TrialBannerService } from '@app/shell/trial-banner/service/trial-banner.service';
import {
    Component,
    OnInit,
    OnDestroy,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Input,
    createEnvironmentInjector,
    ApplicationRef,
    inject,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { LeaderboardPreviewGamificationService } from '../../service/leaderboard-preview-gamification/leaderboard-preview-gamification.service';
import { TranslateService } from '@ngx-translate/core';
import { UserSimpleGamification } from '@app/leaderboard/model/user.model';
import { map } from 'rxjs/operators';
import { AccessibilityService } from '@app/tw-shared/services';

@Component({
    selector: 'app-leaderboard-gamification-horizontal',
    templateUrl: './leaderboard-gamification-horizontal.component.html',
    styleUrls: ['./leaderboard-gamification-horizontal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class LeaderboardGamificationHorizontalComponent implements OnInit, OnDestroy {
    /**
     * Count of users in leadearboard preview
     */
    @Input() count!: number;
    @Input() name!: string;
    @Input() size!: string;

    private readonly appRef = inject(ApplicationRef);
    private readonly leaderboardPreviewGamificationService = inject(LeaderboardPreviewGamificationService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);
    private readonly trialBannerService = inject(TrialBannerService);
    private readonly popupService = inject(PopupService);
    private readonly accessibilityService = inject(AccessibilityService);

    readonly isVeryLargeFontSize$ = this.accessibilityService.isVeryLargeFontSize$;

    itemsCount = 4;

    /**
     * Subscription
     */
    private subscriptions = new Subscription();

    readonly form = this.leaderboardPreviewGamificationService.form;

    readonly range = this.form.controls.range;

    ngOnInit() {
        this.subscriptions.add(this.leaderboardPreviewGamificationService.updates.subscribe(() => this.changeDetectorRef.detectChanges()));

        // Trial
        if (this.hasTrial) {
            this.subscriptions.add(this.invitesTrial$.subscribe(() => this.changeDetectorRef.detectChanges()));
            this.subscriptions.add(
                this.trialBannerService.updateLeaderboard$.subscribe(() => this.leaderboardPreviewGamificationService.trialResetLeaderboard()),
            );
            if (this.trialBannerService.invitesTrial.loaded) {
                this.trialBannerService.getTrial(true);
            }
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    getTrendColor(trend: string) {
        switch (trend) {
            case 'success':
                return 'primary';
            case 'muted':
                return 'black';
            case 'muted-2':
                return 'black-10';
            case 'danger':
                return 'danger';
        }
    }

    /**
     * Leaderboard
     */
    get leaderboard() {
        return this.leaderboardPreviewGamificationService.leaderboard(this.itemsCount);
    }

    /**
     * Ranges
     */
    get loading() {
        return this.leaderboardPreviewGamificationService.loading;
    }

    /**
     * Dropdown Items
     */
    get dropdownItems() {
        return this.leaderboardPreviewGamificationService.ranges.map((name) => {
            return {
                id: name,
                name: this.rangeLabel(name),
            };
        });
    }

    get hasTrial() {
        return this.trialBannerService.hasTrial;
    }

    get invitesTrial$() {
        return this.trialBannerService.invitesTrial$;
    }

    get invitesLoading$() {
        return this.invitesTrial$.pipe(map((data) => data.loading));
    }

    get sendedAllInvites() {
        const invites = this.trialBannerService.invitesTrial;
        return invites.max === invites.sent;
    }

    get showTrialInvite() {
        return this.hasTrial && !this.sendedAllInvites;
    }

    get showTrialInvitesInfo() {
        return this.hasTrial && this.leaderboard && this.leaderboard.length === 1;
    }

    get hasColleagues() {
        return this.leaderboardPreviewGamificationService.leaderboard(5)?.length > 1;
    }

    get showFullLeaderboardBtn() {
        return !this.hasTrial || (this.hasTrial && this.hasColleagues);
    }

    inviteTrialUsers() {
        if (!this.trialBannerService.invitesTrial.loading) {
            const environmentInjector = createEnvironmentInjector([{ provide: TrialBannerService, useValue: this.trialBannerService }], this.appRef.injector);

            this.popupService.open(LeaderboardTrialInvitesPopupComponent, null, null, { injector: environmentInjector });
        }
    }

    /**
     * returns user's fullname (name + surname) or "me" if he/she is currently logged in
     * @param user user data
     */
    getUserFullName(user: UserSimpleGamification) {
        if (user.me) {
            return this.translateService.instant('v3-globale-me');
        }
        return `${user.firstname} ${user.surname}`;
    }

    /**
     * Range Label
     */
    private rangeLabel(key: string) {
        switch (key) {
            case 'week':
                return this.translateService.instant('v3-preview-leaderboard-range-week');
            case 'month':
                return this.translateService.instant('v3-preview-leaderboard-range-month');
            case 'all-time':
                return this.translateService.instant('v3-preview-leaderboard-range-all-time');
            case 'alltime':
                return this.translateService.instant('v3-preview-leaderboard-range-alltime');
            case 'yeartodate':
                return this.translateService.instant('v3-preview-leaderboard-range-year-to-date');
        }
    }
}
